/**
 * USF Custom Configuration - Clean Version with Currency Format Debugging
 * This file customizes the Ultimate Search & Filter app behavior without price overrides
 */

(function() {
  'use strict';
  
  
  
  // ========================================
  // UTILITY: Page Type Checks
  // ========================================
  
  /**
   * Check if USF should run on the current page
   * USF is only needed on collection/search pages, not product or cart pages
   */
  function shouldRunUSFOnPage() {
    const pathname = window.location.pathname;
    const isProductPage = pathname.includes('/products/');
    const isCartPage = pathname === '/cart' || pathname.startsWith('/cart/');
    
    if (isCartPage) {
      console.log('ℹ️ USF disabled on cart page');
      return false;
    }
    
    if (isProductPage) {
      console.log('ℹ️ USF disabled on product page');
      return false;
    }
    
    return true;
  }
  
  // ========================================
  // UTILITY: Reliable Async Helpers
  // ========================================
  
  /**
   * Wait for a condition to be true using RAF (more reliable than setTimeout)
   */
  function waitFor(conditionFn, options = {}) {
    const { timeout = 10000, description = 'Condition' } = options;
    
    return new Promise((resolve, reject) => {
      if (conditionFn()) {
        resolve();
        return;
      }
      
      const startTime = Date.now();
      let rafId = null;
      
      function check() {
        if (conditionFn()) {
          if (rafId) cancelAnimationFrame(rafId);
          resolve();
        } else if (Date.now() - startTime > timeout) {
          if (rafId) cancelAnimationFrame(rafId);
          reject(new Error(`${description} not ready after ${timeout}ms`));
        } else {
          rafId = requestAnimationFrame(check);
        }
      }
      
      rafId = requestAnimationFrame(check);
    });
  }
  
  /**
   * Wait for a DOM element to appear using MutationObserver
   */
  function waitForElement(selector, options = {}) {
    const { timeout = 10000, parent = document.body } = options;
    
    return new Promise((resolve, reject) => {
      const element = parent.querySelector(selector);
      if (element) {
        resolve(element);
        return;
      }
      
      const startTime = Date.now();
      
      const observer = new MutationObserver(() => {
        const element = parent.querySelector(selector);
        if (element) {
          observer.disconnect();
          resolve(element);
        } else if (Date.now() - startTime > timeout) {
          observer.disconnect();
          reject(new Error(`Element ${selector} not found after ${timeout}ms`));
        }
      });
      
      observer.observe(parent, {
        childList: true,
        subtree: true
      });
    });
  }
  
  /**
   * Debounce using RAF
   */
  function debounce(func, wait = 100) {
    let timeout;
    let rafId;
    
    return function(...args) {
      const later = () => {
        clearTimeout(timeout);
        if (rafId) cancelAnimationFrame(rafId);
        func.apply(this, args);
      };
      
      clearTimeout(timeout);
      if (rafId) cancelAnimationFrame(rafId);
      
      timeout = setTimeout(() => {
        rafId = requestAnimationFrame(later);
      }, wait);
    };
  }
  
  /**
   * Delay using RAF (more reliable than setTimeout for next frame)
   */
  function nextFrame() {
    return new Promise(resolve => requestAnimationFrame(resolve));
  }
  
  /**
   * Delay with Promise
   */
  function delay(ms) {
    return new Promise(resolve => setTimeout(resolve, ms));
  }
  
  // Check if we're on a product page and monitor for any issues
  if (window.location.pathname.includes('/products/')) {
    console.log('📦 Product page detected, monitoring for variant/form issues...');
    
    // Monitor for any JavaScript errors that might affect product forms
    window.addEventListener('error', function(e) {
      if (e.message.includes('product') || e.message.includes('variant') || e.message.includes('form')) {
        console.error('🚨 Product-related JavaScript error detected:', e.message);
      }
    });
  }

  // Simple initialization - no debugging needed since the fix works

  // Fix USF currency formatting to preserve amounts and add currency codes
  function fixUSFCurrencyConverter() {
    if (typeof window.usf !== 'undefined' && window.usf.currencyConverter) {
      // Only apply currency fix on collection/search pages, not product pages
      const isCollectionPage = window.location.pathname.includes('/collections/') || 
                               window.location.pathname.includes('/search') ||
                               document.querySelector('#usf_container');
      
      if (!isCollectionPage) {
        console.log('🔧 Skipping currency fix - not on a USF page');
        return;
      }
      
      console.log('🔧 Applying currency fix for USF pages');
      // Fixing USF currency converter to add currency codes
      
      // Store the original USF format function
      if (!window.usf.currencyConverter._originalFormat) {
        window.usf.currencyConverter._originalFormat = window.usf.currencyConverter.format;
      }
      
      // Override USF's format function to use our custom formatter
      window.usf.currencyConverter.format = function(amount, useLongFormat) {
        try {
          // Get current currency from Shopify
          const currentCurrency = window.Shopify?.currency?.active || 'AUD';
          
          // Use the custom _usfFormatPrice function if available
          if (typeof window._usfFormatPrice === 'function') {
            return window._usfFormatPrice(amount, currentCurrency);
          }
          
          // Fallback: Get the original formatted result
          let originalResult;
          if (window.usf.currencyConverter._originalFormat) {
            originalResult = window.usf.currencyConverter._originalFormat(amount, useLongFormat);
          } else {
            // Fallback formatting if no original
            originalResult = '$' + (amount / 100).toFixed(2);
          }
          
          // Simply append the currency code to the original result as fallback
          const result = `${originalResult} ${currentCurrency}`;
          return result;
          
        } catch (e) {
          console.error('Error formatting currency:', e);
          // Fallback to original if it exists
          if (window.usf.currencyConverter._originalFormat) {
            return window.usf.currencyConverter._originalFormat(amount, useLongFormat);
          }
          // Final fallback
          return '$' + (amount / 100).toFixed(2);
        }
      };
      
      // Currency converter successfully updated
    }
  }

  // Apply the fix when USF is ready
  async function applyFixWhenReady() {
    try {
      await waitFor(
        () => typeof window.usf !== 'undefined' && window.usf.currencyConverter,
        { timeout: 10000, description: 'USF currency converter' }
      );
      fixUSFCurrencyConverter();
    } catch (error) {
      console.log('⚠️ USF currency converter not ready');
    }
  }

  // Apply fix on USF events
  document.addEventListener('usf:ready', fixUSFCurrencyConverter);
  document.addEventListener('usf:initialized', fixUSFCurrencyConverter);
  
  // Start trying to apply the fix
  applyFixWhenReady();

  // ===== USF IMAGE COMPONENT FIXES =====
  // Fix missing Vue computed properties for product images to prevent broken thumbnails
  
  async function addUSFImageComputedProperties() {
    // Wait for USF to initialize
    try {
      await waitFor(
        () => typeof window.usf !== 'undefined' && window.usf.register,
        { timeout: 10000, description: 'USF register' }
      );
    } catch (error) {
      console.log('⚠️ USF not ready for image properties');
      return;
    }

    // Check if TaigGridItem component exists and needs the fix
    if (window.usf && window.usf.components && window.usf.components['usf-taig-griditem']) {
      const component = window.usf.components['usf-taig-griditem'];
      
      // Check if computed properties are missing
      if (!component.computed || !component.computed.selectedImageUrl) {
        console.log('🖼️ Adding missing image computed properties to TaigGridItem');
        
        // Ensure computed object exists
        if (!component.computed) {
          component.computed = {};
        }
        
        // Add missing computed properties for images
        Object.assign(component.computed, {
          selectedImage() {
            if (!this.product || !this.product.images || !this.product.images.length) {
              return null;
            }
            // Return the first image or selected variant's image
            if (this.product.selectedVariantId && this.product.variants) {
              const selectedVariant = this.product.variants.find(v => v.id == this.product.selectedVariantId);
              if (selectedVariant && selectedVariant.imageIndex !== undefined && this.product.images[selectedVariant.imageIndex]) {
                return this.product.images[selectedVariant.imageIndex];
              }
            }
            return this.product.images[0];
          },
          
          selectedImageUrl() {
            if (!this.selectedImage || !this.selectedImage.url) {
              return '';
            }
            return window.usf.platform.getImageUrl ? window.usf.platform.getImageUrl(this.selectedImage.url, 600) : this.selectedImage.url;
          },
          
          scaledSelectedImageUrl() {
            if (!this.selectedImage || !this.selectedImage.url) {
              return '';
            }
            return window.usf.platform.getImageUrl ? window.usf.platform.getImageUrl(this.selectedImage.url, '{size}') : this.selectedImage.url;
          },
          
          hoverImage() {
            if (!this.product || !this.product.images || this.product.images.length < 2) {
              return null;
            }
            // Return the second image for hover effect
            return this.product.images[1];
          },
          
          hoverImageUrl() {
            if (!this.hoverImage || !this.hoverImage.url) {
              return '';
            }
            return window.usf.platform.getImageUrl ? window.usf.platform.getImageUrl(this.hoverImage.url, 600) : this.hoverImage.url;
          },
          
          scaledHoverImageUrl() {
            if (!this.hoverImage || !this.hoverImage.url) {
              return '';
            }
            return window.usf.platform.getImageUrl ? window.usf.platform.getImageUrl(this.hoverImage.url, '{size}') : this.hoverImage.url;
          }
        });
        
        // Ensure methods object exists and add image methods
        if (!component.methods) {
          component.methods = {};
        }
        
        Object.assign(component.methods, {
          getSelectedImageUrl(size) {
            if (!this.selectedImage || !this.selectedImage.url) {
              return '';
            }
            return window.usf.platform.getImageUrl ? window.usf.platform.getImageUrl(this.selectedImage.url, size || 600) : this.selectedImage.url;
          },
          
          getHoverImageUrl(size) {
            if (!this.hoverImage || !this.hoverImage.url) {
              return '';
            }
            return window.usf.platform.getImageUrl ? window.usf.platform.getImageUrl(this.hoverImage.url, size || 600) : this.hoverImage.url;
          }
        });
        
        console.log('✅ Successfully added missing image computed properties to TaigGridItem');
      } else {
        console.log('ℹ️ TaigGridItem already has image computed properties');
      }
    } else {
      // Component not found yet, try again later
      await delay(1000);
      await addUSFImageComputedProperties();
    }
  }

  // Initialize image fixes
  addUSFImageComputedProperties();
  
  // Also apply on USF events
  document.addEventListener('usf:ready', addUSFImageComputedProperties);
  document.addEventListener('usf:initialized', addUSFImageComputedProperties);

  // ===== USF COLLECTION FILTER SYNC =====
  // This section handles syncing filter parameters across collection navigation

  /**
   * Extract USF filter parameters from current URL
   * @returns {Object} Object containing filter parameters and metadata
   */
  function extractUSFFilters() {
    const url = new URL(window.location.href);
    const usfFilters = {};
    let hasFilters = false;

    // Debug: Log all URL parameters
    console.log('🔍 Extracting USF filters from URL:', window.location.href);
    console.log('📋 All URL parameters:', Array.from(url.searchParams.entries()));

    // Extract all USF filter parameters (those starting with 'uff_')
    for (const [key, value] of url.searchParams.entries()) {
      console.log(`  Checking param: ${key} = ${value}, starts with uff_: ${key.startsWith('uff_')}`);
      if (key.startsWith('uff_')) {
        usfFilters[key] = value;
        hasFilters = true;
        console.log(`  ✅ Added filter: ${key} = ${value}`);
      }
    }

    // Also capture other relevant parameters
    const sortBy = url.searchParams.get('sort_by');
    const view = url.searchParams.get('view');

    console.log('📊 Extracted filters:', {
      filterCount: Object.keys(usfFilters).length,
      filters: usfFilters,
      hasFilters
    });

    return {
      filters: usfFilters,
      sortBy: sortBy,
      view: view,
      hasFilters: hasFilters
    };
  }

  /**
   * Parse USF filter parameter to extract filter type and values
   * @param {string} key - The USF filter key (e.g., 'uff_1ns84p_option%3ASize')
   * @param {string} value - The filter value (e.g., '32A_usf_32B')
   * @returns {Object} Parsed filter information
   */
  function parseUSFFilter(key, value) {
    // Extract the filter type from the key (after the ID part)
    const parts = key.split('_');
    if (parts.length < 3) return null;
    
    // Remove 'uff' and the collection ID to get the filter type
    const filterType = parts.slice(2).join('_');
    
    // Parse the values (split by '_usf_')
    const values = value.split('_usf_');
    
    return {
      originalKey: key,
      collectionId: parts[1], // e.g., '1ns84p' or 'g6x51l'
      filterType: filterType, // e.g., 'option%3ASize'
      values: values,
      originalValue: value
    };
  }

  /**
   * Apply URL filters to current USF instance (for destination pages)
   */
  async function applyURLFiltersToUSF() {
    // Only run on collection pages with USF
    if (!window.location.pathname.includes('/collections/') || typeof window.usf === 'undefined') {
      return;
    }

    console.log('🔍 Checking for URL filters to apply to USF...');
    
    const filterState = extractUSFFilters();
    if (!filterState.hasFilters) {
      console.log('ℹ️ No URL filters found to apply');
      return;
    }

    // Wait for USF to be fully ready AND have all facets loaded (not filtered yet)
    await delay(1500);
    
    if (!window.usf || !window.usf.queryRewriter) {
      console.log('⚠️ USF not ready yet, will retry...');
      await delay(1000);
      await applyURLFiltersToUSF();
      return;
    }
      
      // IMPORTANT: Check if we have all facets loaded before applying filters
      // If we only have 2 facets, USF might have already applied filters from URL
      // Wait a bit longer for USF to load all facets
      if (window.usf.search && window.usf.search.result && window.usf.search.result.facets) {
        const facetCount = window.usf.search.result.facets.length;
        console.log(`📋 Current facet count: ${facetCount}`);
        
        // If we have very few facets, USF might have already pre-filtered
        // Skip applying filters and go straight to cleanup
        if (facetCount < 5) {
          console.log('⚠️ Too few facets detected - USF may have already applied URL filters');
          console.log('⏭️ Skipping filter application, will run cleanup instead');
          await delay(1000);
          checkAndCleanUnavailableFilters();
          return;
        }
      }

      console.log('🚀 Applying URL filters to USF:', filterState.filters);
      
      // Check if USF has already applied the filters from URL (by comparing filter values)
      // If filters are already applied, we don't need to refresh - this prevents new facet IDs
      const currentFilters = window.usf.queryRewriter.facetFilters || {};
      const currentFilterValues = Object.values(currentFilters).map(f => f[1] || []).flat();
      
      // Parse URL filter values
      const urlFilterValues = [];
      for (const [key, value] of Object.entries(filterState.filters)) {
        const parsed = parseUSFFilter(key, value);
        if (parsed && parsed.values) {
          urlFilterValues.push(...parsed.values);
        }
      }
      
      // Check if all URL filter values are already in current filters
      const allValuesMatch = urlFilterValues.length > 0 && 
        urlFilterValues.every(urlValue => 
          currentFilterValues.some(currentValue => 
            currentValue === urlValue || 
            currentValue.replace(/_usf_/g, ' ') === urlValue.replace(/_usf_/g, ' ')
          )
        );
      
      if (allValuesMatch && Object.keys(currentFilters).length > 0) {
        console.log('✅ Filters already applied by USF - values match current state');
        console.log('⏭️ Skipping refresh to avoid generating new facet IDs');
        console.log('   URL values:', urlFilterValues);
        console.log('   Current values:', currentFilterValues);
        
        // Just clean up any duplicate facet IDs in URL (old vs new IDs with same values)
        await delay(500);
        cleanupDuplicateFilterParams();
        return;
      }
      
      console.log('⚠️ Filters not yet applied - will apply and refresh...');
      
      // Parse all URL filters
      const parsedFilters = [];
      for (const [key, value] of Object.entries(filterState.filters)) {
        const parsed = parseUSFFilter(key, value);
        if (parsed) {
          parsedFilters.push(parsed);
          console.log('📋 Parsed filter:', {
            type: parsed.filterType,
            values: parsed.values,
            fromCollection: parsed.collectionId
          });
        }
      }

      if (parsedFilters.length === 0) {
        console.log('⚠️ No valid filters found to apply');
        return;
      }

      // Try to apply filters to current USF instance
      try {
        // Get current USF facet filters
        let currentFilters = window.usf.queryRewriter.facetFilters || {};
        
        // Find matching facets in current collection and apply values
        if (window.usf.search && window.usf.search.result && window.usf.search.result.facets) {
          const currentFacets = window.usf.search.result.facets;
          
          console.log('📋 Current facets count:', window.usf.search.result.facets.length);
          console.log('📋 Current facets:', window.usf.search.result.facets.map(f => ({
            facetName: f.facetName,
            title: f.title,
            id: f.id,
            valueCount: f.values ? f.values.length : 0
          })));
          
          parsedFilters.forEach(filter => {
            // Try to find matching facet by filter type
            const matchingFacet = currentFacets.find(facet => {
              // Match by facet name or similar criteria
              const facetKey = `option%3A${facet.facetName}`;
              return filter.filterType.includes(facetKey) || 
                     filter.filterType.includes(facet.facetName);
            });
            
            if (matchingFacet) {
              console.log('✅ Found matching facet:', matchingFacet.facetName, 'for filter:', filter.filterType);
              
              // Apply the filter values to this facet
              currentFilters[matchingFacet.id] = [matchingFacet.facetName, filter.values];
              
              console.log('🔧 Applied filter to facet ID:', matchingFacet.id, 'with values:', filter.values);
            } else {
              console.log('❌ No matching facet found for filter type:', filter.filterType);
            }
          });
          
          // Update USF with the new filters
          window.usf.queryRewriter.facetFilters = currentFilters;
          
          // Remove old filter parameters BEFORE USF refresh to prevent duplicates
          // This ensures USF only adds its new facet IDs to a clean URL
          console.log('🧹 Removing old filter parameters before refresh...');
          const currentUrl = new URL(window.location.href);
          let removedParams = [];
          
          // Remove all existing uff_ parameters (old facet IDs from previous page)
          for (const key of Array.from(currentUrl.searchParams.keys())) {
            if (key.startsWith('uff_')) {
              removedParams.push(key);
              currentUrl.searchParams.delete(key);
            }
          }
          
          if (removedParams.length > 0) {
            console.log(`  🗑️ Removed ${removedParams.length} old parameter(s):`, removedParams);
            // Update URL without triggering navigation
            window.history.replaceState({}, '', currentUrl.toString());
          }
          
          console.log('✅ Applied filters to USF internal state');
          
          // Trigger USF to refresh with new filters
          // USF will now add fresh facet IDs to the clean URL
          if (window.usf.search && window.usf.search.refresh) {
            console.log('🔄 Refreshing USF with applied filters');
            await nextFrame();
            window.usf.search.refresh();
          }
          
            console.log('✅ Successfully applied URL filters to USF');
            
            // Check if filters resulted in no products and clean up if needed
            await delay(2000);
            checkAndCleanUnavailableFilters();
          }
          
        } catch (error) {
          console.error('❌ Error applying URL filters to USF:', error);
        }
    }

    /**
     * Check each filter against available facets and remove those that don't exist
     * or don't have the specific value in the current collection
     */
    function checkAndCleanUnavailableFilters() {
      // Only run on collection pages
      if (!window.location.pathname.includes('/collections/')) {
        return;
      }

      console.log('🧹 Checking filters against available facets...');

      // Check if USF is ready with facet data
      if (!window.usf || !window.usf.search || !window.usf.search.result || !window.usf.search.result.facets) {
        console.log('⚠️ USF not ready yet, skipping cleanup');
        return;
      }

      const filterState = extractUSFFilters();
      if (!filterState.hasFilters) {
        console.log('ℹ️ No filters to check');
        return;
      }

      const resultCount = window.usf.search.result.total || 0;
      console.log(`📊 Current result count: ${resultCount}`);

      // If we have results, filters are working correctly - no cleanup needed!
      if (resultCount > 0) {
        console.log('✅ Filters are working (have results), no cleanup needed');
        return;
      }

      // SPECIAL CASE: If we have 0 results, the facets returned are unreliable
      // USF will only show facets that would reduce the 0 results, which doesn't help us
      // So we remove the FIRST filter and try again
      console.log('⚠️ 0 results detected - current facet data is unreliable');
      console.log('💡 Strategy: Remove first filter to allow USF to show real facets');
      
      const filterEntries = Object.entries(filterState.filters);
      const [firstKey, firstValue] = filterEntries[0];
      const parsed = parseUSFFilter(firstKey, firstValue);
      
      if (parsed) {
        const filterType = decodeURIComponent(parsed.filterType);
        console.log(`🗑️ Removing first filter (${filterType}=${parsed.values.join(',')}) to test remaining filters`);
        
        const newUrl = new URL(window.location.href);
        newUrl.searchParams.delete(firstKey);
        
        console.log('🔄 Updating URL and refreshing...');
        window.history.replaceState({}, '', newUrl.toString());
        
        // Refresh USF
        if (window.usf && window.usf.search && typeof window.usf.search.refresh === 'function') {
          requestAnimationFrame(() => window.usf.search.refresh());
        }
      }
      return;

      // Note: The code below (facet checking) is now unreachable and only kept for reference
      // If we have results, we don't clean up
      const facets = window.usf.search.result.facets;
      console.log('📊 Checking', Object.keys(filterState.filters).length, 'filters against', facets.length, 'available facets');

      // Build a map of available facets and their values
      const facetMap = {};
      facets.forEach(facet => {
        if (facet.facetName) {
          facetMap[facet.facetName] = {
            title: facet.title,
            values: facet.values || []
          };
          console.log(`  📋 Facet "${facet.facetName}" (${facet.title}): ${facetMap[facet.facetName].values.length} values`);
        }
      });

      const validFilters = {};
      const invalidFilters = {};

      // Check each filter
      for (const [key, value] of Object.entries(filterState.filters)) {
        const parsed = parseUSFFilter(key, value);
        if (!parsed) continue;

        const filterType = decodeURIComponent(parsed.filterType);
        console.log(`\n  🔍 Checking filter: ${filterType}=${parsed.values.join(',')}`);

        // Step 1: Check if the facet type exists
        const matchingFacet = facetMap[filterType];
        
        if (!matchingFacet) {
          console.log(`    ❌ Facet type "${filterType}" does NOT exist in this collection - REMOVE`);
          invalidFilters[key] = value;
          continue;
        }

        console.log(`    ✅ Facet type "${filterType}" exists`);

        // Step 2: Check if the filter value(s) exist in the facet's available values
        const availableValues = matchingFacet.values.map(v => {
          // Handle different value formats
          if (typeof v === 'string') return v.toLowerCase();
          if (v.value) return String(v.value).toLowerCase();
          if (v.label) return String(v.label).toLowerCase();
          return String(v).toLowerCase();
        });

        console.log(`    📝 Available values (${availableValues.length}):`, availableValues.slice(0, 10));

        // Check if ANY of the filter values exist in available values
        const filterValues = parsed.values.map(v => String(v).toLowerCase());
        const hasValidValue = filterValues.some(fv => 
          availableValues.some(av => av === fv || av.includes(fv) || fv.includes(av))
        );

        if (hasValidValue) {
          console.log(`    ✅ Value "${parsed.values.join(',')}" IS in the filter list - KEEP`);
          validFilters[key] = value;
        } else {
          console.log(`    ❌ Value "${parsed.values.join(',')}" is NOT in the filter list - REMOVE`);
          invalidFilters[key] = value;
        }
      }

      // If there are invalid filters, remove them
      if (Object.keys(invalidFilters).length > 0) {
        console.log('\n🧹 Removing', Object.keys(invalidFilters).length, 'invalid filter(s)');
        
        const newUrl = new URL(window.location.href);
        
        // Remove invalid filter parameters
        for (const key of Object.keys(invalidFilters)) {
          newUrl.searchParams.delete(key);
          console.log(`  🗑️ Removed: ${key}`);
        }

        if (newUrl.toString() !== window.location.href) {
          console.log('🔄 Updating URL and refreshing with valid filters only');
          window.history.replaceState({}, '', newUrl.toString());
          
          // Refresh USF
          if (window.usf && window.usf.search && typeof window.usf.search.refresh === 'function') {
            requestAnimationFrame(() => window.usf.search.refresh());
          }
        }
      } else {
        console.log('\n✅ All filters are valid!');
      }
    }

    /**
     * Remove unavailable filter parameters and redirect to clean URL
     * Uses iterative approach: remove filters one by one to find the culprit
     */
    function cleanUnavailableFilters() {
      const currentUrl = new URL(window.location.href);
      const filterState = extractUSFFilters();
      
      if (!filterState.hasFilters) {
        console.log('ℹ️ No filters to clean');
        return;
      }

      console.log('🧹 Cleaning unavailable filters from URL');
      console.log('🔍 Strategy: Test each filter individually to find which one causes no results');

      // Parse all current filters
      const parsedFilters = [];
      for (const [key, value] of Object.entries(filterState.filters)) {
        const parsed = parseUSFFilter(key, value);
        if (parsed) {
          parsedFilters.push({ ...parsed, originalKey: key, originalValue: value });
        }
      }

      console.log('📋 Testing filters:', parsedFilters.map(f => `${f.filterType}=${f.values.join(',')}`));

      // Test each filter individually by temporarily removing others
      const filterTestResults = {};
      
      parsedFilters.forEach((filter, index) => {
        const testUrl = new URL(window.location.pathname, window.location.origin);
        
        // Add ONLY this filter to the test URL
        testUrl.searchParams.set(filter.originalKey, filter.originalValue);
        
        console.log(`🧪 Testing filter ${index + 1}/${parsedFilters.length}: ${filter.filterType}`);
        
        // We can't actually navigate, so we'll use a different approach:
        // Check if the facet exists and has the value when NO filters are applied
        // This requires checking USF's unfiltered facet data
        
        const decodedFilterType = decodeURIComponent(filter.filterType);
        filterTestResults[filter.originalKey] = {
          filter: filter,
          shouldKeep: false, // Will be determined below
          reason: ''
        };
      });

      // Since we can't test by navigation, use the current facet state
      // But recognize that current facets are AFTER all filters applied
      // Solution: Remove filters one at a time and keep those that exist in facets
      
      console.log('📋 Current facet state (after all filters applied):');
      
      let availableFilterValues = {};
      
      if (window.usf && window.usf.search && window.usf.search.result && window.usf.search.result.facets) {
        const facets = window.usf.search.result.facets;
        
        console.log('📋 Facets:', facets.map(f => ({
          facetName: f.facetName,
          title: f.title,
          valueCount: f.values ? f.values.length : 0
        })));
        
        facets.forEach(facet => {
          if (facet.facetName && facet.values && Array.isArray(facet.values) && facet.values.length > 0) {
            availableFilterValues[facet.facetName] = facet.values.map(v => v.value || v.label || v);
          }
        });
      }

      // Strategy: If a facet exists but has 0 values, it's likely the problematic one
      // If a facet doesn't exist at all, it might be hidden due to another filter
      const validFilters = {};
      const problematicFilters = [];

      for (const [key, value] of Object.entries(filterState.filters)) {
        const parsed = parseUSFFilter(key, value);
        if (!parsed) continue;

        const decodedFilterType = decodeURIComponent(parsed.filterType);
        
        // Try to find matching facet
        let matchingFacetName = null;
        let facetHasValues = false;
        
        if (window.usf && window.usf.search && window.usf.search.result && window.usf.search.result.facets) {
          const facets = window.usf.search.result.facets;
          
          for (const facet of facets) {
            const facetName = facet.facetName;
            if (decodedFilterType.includes(facetName) || 
                decodedFilterType.endsWith(':' + facetName) ||
                facetName === decodedFilterType) {
              matchingFacetName = facetName;
              facetHasValues = facet.values && facet.values.length > 0;
              
              console.log(`🔍 Filter: ${decodedFilterType}`);
              console.log(`   → Matched facet: ${facetName}`);
              console.log(`   → Facet has values: ${facetHasValues}`);
              console.log(`   → Value count: ${facet.values ? facet.values.length : 0}`);
              
              // If facet exists but has 0 values, this filter is causing "no results"
              if (!facetHasValues) {
                console.log(`   ❌ PROBLEMATIC: Facet exists but has no values - this filter causes no results`);
                problematicFilters.push({
                  key,
                  filterType: decodedFilterType,
                  values: parsed.values,
                  reason: 'Facet has no values'
                });
              } else {
                console.log(`   ✅ VALID: Facet has values - keep this filter`);
                validFilters[key] = value;
              }
              
              break;
            }
          }
          
          // If no matching facet found, the facet might be hidden due to another filter
          // In this case, we should keep it for now and let the problematic filter be removed first
          if (!matchingFacetName) {
            console.log(`🔍 Filter: ${decodedFilterType}`);
            console.log(`   → No matching facet (might be hidden by another filter)`);
            console.log(`   ⏸️  KEEP for now: Will check after removing problematic filters`);
            validFilters[key] = value;
          }
        }
      }

      // Summary
      console.log('📊 Filter analysis complete:');
      console.log(`   ✅ Valid filters: ${Object.keys(validFilters).length}`);
      console.log(`   ❌ Problematic filters: ${problematicFilters.length}`);
      
      if (problematicFilters.length > 0) {
        console.log('🚫 Removing problematic filters:');
        problematicFilters.forEach(f => {
          console.log(`   - ${f.filterType} = ${f.values.join(', ')} (${f.reason})`);
        });
      }

      // Clean up duplicate filter parameters (remove old collection IDs)
      const cleanedFilters = {};
      const filtersByType = {};

      // Group filters by type to remove duplicates
      for (const [key, value] of Object.entries(validFilters)) {
        const parsed = parseUSFFilter(key, value);
        if (parsed) {
          const filterType = parsed.filterType;
          
          // Keep only the most recent or current collection's filter
          if (!filtersByType[filterType] || 
              key.includes(window.location.pathname.split('/').pop())) {
            filtersByType[filterType] = { key, value };
          }
        }
      }

      // Build final cleaned filters
      for (const filterData of Object.values(filtersByType)) {
        cleanedFilters[filterData.key] = filterData.value;
      }

      console.log('🔧 Final filter count:', Object.keys(cleanedFilters).length);

      // Build new URL with cleaned filters
      const newUrl = new URL(window.location.pathname, window.location.origin);
      
      // Add cleaned filters
      for (const [key, value] of Object.entries(cleanedFilters)) {
        newUrl.searchParams.set(key, value);
      }
      
      // Preserve other parameters
      if (filterState.sortBy) {
        newUrl.searchParams.set('sort_by', filterState.sortBy);
      }
      if (filterState.view) {
        newUrl.searchParams.set('view', filterState.view);
      }

      const newUrlString = newUrl.toString();
      const currentUrlString = window.location.href;

      if (newUrlString !== currentUrlString) {
        console.log('🔄 Updating URL to cleaned version:', newUrlString);
        console.log('   Old URL:', currentUrlString);
        console.log('   New URL:', newUrlString);
        
        // Use replaceState to avoid adding to browser history
        window.history.replaceState({}, '', newUrlString);
        
        // Trigger USF to refresh with the cleaned URL instead of full page reload
        if (window.usf && window.usf.search && window.usf.search.refresh) {
          console.log('🔄 Refreshing USF search with cleaned filters');
          requestAnimationFrame(() => window.usf.search.refresh());
        } else {
          console.log('⚠️ USF search refresh not available, may need manual reload');
        }
      } else {
        console.log('ℹ️ URL is already clean, no changes needed');
      }
    }

  /**
   * Update collection card URLs with current filter parameters
   */
  function syncFiltersToCollectionCards() {
    // Only proceed if we have collection cards
    const collectionCards = document.querySelectorAll('a.card--collection');
    if (!collectionCards.length) {
      console.log('ℹ️ No collection cards found to update');
      return;
    }

    // Extract current filter state
    const filterState = extractUSFFilters();
    
    console.log('🔗 Syncing filters to collection cards:', {
      filterCount: Object.keys(filterState.filters).length,
      hasFilters: filterState.hasFilters,
      sortBy: filterState.sortBy,
      view: filterState.view
    });

    // Update each collection card
    collectionCards.forEach((card, index) => {
      try {
        // Store original URL if not already stored
        if (!card.hasAttribute('data-original-href')) {
          card.setAttribute('data-original-href', card.href);
        }

        const originalHref = card.getAttribute('data-original-href');
        const cardUrl = new URL(originalHref, window.location.origin);
        
        // Clear any existing filter parameters
        const newSearchParams = new URLSearchParams();
        
        // Add current USF filters
        for (const [key, value] of Object.entries(filterState.filters)) {
          newSearchParams.set(key, value);
        }
        
        // Add sort parameter if present
        if (filterState.sortBy) {
          newSearchParams.set('sort_by', filterState.sortBy);
        }
        
        // Add view parameter if present
        if (filterState.view) {
          newSearchParams.set('view', filterState.view);
        }

        // Construct new URL
        const queryString = newSearchParams.toString();
        const newHref = cardUrl.pathname + (queryString ? '?' + queryString : '');
        
        // Update the card's href
        card.href = newHref;
        
       // console.log(`🔗 Updated collection card ${index + 1}: ${originalHref} → ${newHref}`);
        
      } catch (error) {
        console.error(`❌ Error updating collection card ${index + 1}:`, error);
      }
    });

    
  }

  /**
   * Clean up duplicate USF filter parameters from URL
   * This happens when USF regenerates facet IDs but old ones remain in URL
   */
  function cleanupDuplicateFilterParams() {
    const currentUrl = new URL(window.location.href);
    
    // Group filters by their values to detect duplicates
    const filtersByValue = {};
    const allFilterKeys = [];
    
    // Collect all uff_ parameters
    for (const [key, value] of currentUrl.searchParams.entries()) {
      if (key.startsWith('uff_')) {
        allFilterKeys.push(key);
        
        // Normalize the value for comparison
        const normalizedValue = value.toLowerCase().trim();
        
        if (!filtersByValue[normalizedValue]) {
          filtersByValue[normalizedValue] = [];
        }
        filtersByValue[normalizedValue].push(key);
      }
    }
    
    // Find and remove duplicate filter parameters
    let removedDuplicates = 0;
    const keysToRemove = new Set();
    let hasDuplicates = false;
    
    for (const [value, keys] of Object.entries(filtersByValue)) {
      if (keys.length > 1) {
        // We have duplicates with the same value
        hasDuplicates = true;
        console.log(`🔍 Found ${keys.length} duplicate filters with value: ${value}`);
        console.log('   Keys:', keys);
        
        // Keep only the first one (or the one that matches current facets if available)
        // Remove the rest
        for (let i = 1; i < keys.length; i++) {
          keysToRemove.add(keys[i]);
        }
      }
    }
    
    // If no duplicates found, skip the rest
    if (!hasDuplicates) {
      // No duplicates to clean up
      return;
    }
    
    // Only check for orphaned parameters if we already found duplicates by value
    // This prevents removing valid newly-added parameters
    if (keysToRemove.size > 0 && window.usf && window.usf.queryRewriter && window.usf.queryRewriter.facetFilters) {
      const activeFacetIds = Object.keys(window.usf.queryRewriter.facetFilters);
      
      // Only remove URL parameters that are truly orphaned:
      // 1. They have a duplicate with same value (already in keysToRemove)
      // 2. AND they don't match any active facet
      allFilterKeys.forEach(key => {
        // Only check keys that are already marked for removal
        if (keysToRemove.has(key)) {
          const facetId = key.replace('uff_', '');
          const hasActiveFacet = activeFacetIds.some(activeId => 
            key === `uff_${activeId}` || activeId.includes(facetId)
          );
          
          if (hasActiveFacet) {
            // This key is marked for removal but it HAS an active facet
            // So it's NOT orphaned, remove it from the removal list
            console.log(`✅ Keeping parameter: ${key} (has active facet)`);
            keysToRemove.delete(key);
          } else {
            console.log(`🗑️ Confirmed orphaned parameter: ${key} (no matching active facet)`);
          }
        }
      });
    }
    
    // Remove the identified keys
    if (keysToRemove.size > 0) {
      keysToRemove.forEach(key => {
        console.log(`  🗑️ Removing duplicate/orphaned parameter: ${key}`);
        currentUrl.searchParams.delete(key);
        removedDuplicates++;
      });
      
      console.log(`✅ Cleaned up ${removedDuplicates} duplicate/orphaned filter parameter(s)`);
      
      // Update URL without triggering navigation
      window.history.replaceState({}, '', currentUrl.toString());
    }
  }

  /**
   * Setup USF event listeners for filter changes
   */
  function setupUSFFilterSync() {
    // Check if USF is available
    if (typeof window.usf === 'undefined' || !window.usf.event) {
      console.log('⏳ USF not ready yet, will retry...');
      return false;
    }

    console.log('🎯 Setting up USF filter sync listeners');

    // Listen for USF search results updates (when filters change) - debounced
    const debouncedSync = debounce(syncFiltersToCollectionCards, 200);
    
    window.usf.event.add('sr_updated', function() {
      console.log('📡 USF filters updated - syncing to collection cards');
      debouncedSync();
      
      // Clean up any duplicate filter parameters from URL after a delay
      // to allow USF to finish updating the URL
      setTimeout(cleanupDuplicateFilterParams, 300);
    });

    // Listen for USF mounted event
    window.usf.event.add('mounted', async function() {
      
      await delay(300);
      syncFiltersToCollectionCards();
      
      // Clean up any duplicate filter parameters on mount
      await delay(500);
      cleanupDuplicateFilterParams();
    });

    
    return true;
  }

  /**
   * Initialize the filter sync functionality
   */
  async function initializeFilterSync() {
    

    // Apply URL filters to current USF instance first
    await applyURLFiltersToUSF();

    // Try to setup USF listeners immediately
    if (setupUSFFilterSync()) {
      // USF is ready, run initial sync
      await delay(500);
      syncFiltersToCollectionCards();
    } else {
      // USF not ready, wait for it with backoff
      try {
        await waitFor(
          () => setupUSFFilterSync(),
          { timeout: 10000, description: 'USF filter sync setup' }
        );
        
        // Successfully setup, run initial sync
        await delay(500);
        await applyURLFiltersToUSF();
        syncFiltersToCollectionCards();
      } catch (error) {
        console.log('⚠️ Failed to setup USF filter sync after maximum attempts');
      }
    }

    // Also sync on page load if we already have filters
    const initialFilters = extractUSFFilters();
    if (initialFilters.hasFilters) {
      console.log('🔍 Page loaded with existing filters, syncing collection cards');
      await delay(800);
      syncFiltersToCollectionCards();
    }

    // Listen for browser navigation (back/forward)
    window.addEventListener('popstate', async function() {
      console.log('📡 Browser navigation detected - syncing filters');
      await delay(200);
      await applyURLFiltersToUSF();
      syncFiltersToCollectionCards();
    });
  }

  // Initialize the filter sync functionality - skip on cart/product pages
  if (shouldRunUSFOnPage()) {
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initializeFilterSync);
  } else {
    initializeFilterSync();
    }
  }

  // Expose cleanup function globally for manual triggering
  window.cleanUnavailableUSFFilters = function() {
    console.log('🧹 Manual filter cleanup triggered');
    checkAndCleanUnavailableFilters();
  };


  // Also run on window load as backup
  window.addEventListener('load', async function() {
    const filterState = extractUSFFilters();
    if (filterState.hasFilters) {
      console.log('🌐 Window loaded with filters - final sync check');
      await delay(1000);
      syncFiltersToCollectionCards();
    }
  });

  // ========================================
  // USF PRODUCT CARD COLOR SWATCH FUNCTIONALITY
  // ========================================
  
  // Global cache for custom colors with 5-minute expiration
  let globalCustomColorsCache = null;
  let customColorsFetchPromise = null;
  let isProcessingSwatches = false;
  
  async function fetchCustomColorsOnce() {
    // Return cached data if available and fresh (within 5 minutes)
    if (globalCustomColorsCache && globalCustomColorsCache.data && globalCustomColorsCache.timestamp) {
      const cacheAge = Date.now() - globalCustomColorsCache.timestamp;
      const cacheMaxAge = 5 * 60 * 1000; // 5 minutes
      
      if (cacheAge < cacheMaxAge) {
        return globalCustomColorsCache.data;
      } else {
        globalCustomColorsCache = null;
      }
    }

    // If a fetch is already in progress, wait for it
    if (customColorsFetchPromise) {
      return await customColorsFetchPromise;
    }

    // Start new fetch
    customColorsFetchPromise = (async () => {
      try {
        // Try to use existing functions first
        let fetchCustomColorsFunction = window.usfGetCustomColors || window.fetchCustomColors;
        
        if (fetchCustomColorsFunction && typeof fetchCustomColorsFunction === 'function') {
          // IMPORTANT: Clear any existing cache in the other function to force fresh fetch
          if (window.usfCustomColorsCache) {
            delete window.usfCustomColorsCache;
          }
          
          try {
            const colors = await fetchCustomColorsFunction();
            
            if (colors && colors.length > 0) {
              // Cache the results globally with timestamp
              globalCustomColorsCache = {
                data: colors,
                timestamp: Date.now()
              };
              
              return colors;
            }
          } catch (error) {
            console.error('❌ USF: Custom colors function failed:', error);
          }
        }

        // Fallback implementation
        // console.log('🔄 USF: Using fallback custom colors implementation');
        
        // Use hardcoded API constants (same as other implementations)
        const SHOP_DOMAIN = 'sheer-2.myshopify.com';
        const STOREFRONT_API_TOKEN = 'fd0e2c1c25a2619c0331e759eb5381c5';
        const STOREFRONT_API_URL = `https://${SHOP_DOMAIN}/api/2024-10/graphql.json`;

        // Fetch all colors with pagination support
        let allColors = [];
        let hasNextPage = true;
        let cursor = null;
        let pageCount = 0;
        
        while (hasNextPage && pageCount < 10) { // Safety limit of 10 pages (2500 colors max)
          pageCount++;
          
          const paginatedQuery = `
            {
              metaobjects(type: "custom_color", first: 250${cursor ? `, after: "${cursor}"` : ''}) {
                pageInfo {
                  hasNextPage
                  endCursor
                }
                edges {
                  node {
                    id
                    handle
                    label: field(key: "label") { value }
                    base_color: field(key: "base_color") { value }
                    image: field(key: "image") {
                      reference {
                        ... on MediaImage {
                          image {
                            url
                          }
                        }
                      }
                    }
                  }
                }
              }
            }
          `;
          
          const response = await fetch(window.STOREFRONT_API_URL, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'X-Shopify-Storefront-Access-Token': window.STOREFRONT_API_TOKEN,
            },
            body: JSON.stringify({ query: paginatedQuery }),
          });

          if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
          }

          const result = await response.json();
          
          if (result.errors) {
            console.error('❌ USF: GraphQL errors:', result.errors);
            break;
          }

          if (!result.data || !result.data.metaobjects) {
            console.error('❌ USF: No metaobjects data in response:', result);
            break;
          }
          
          // Add colors from this page
          const pageColors = result.data.metaobjects.edges.map(edge => {
            const node = edge.node;
            return {
              label: node.label?.value,
              base_color: node.base_color?.value,
              image_url: node.image?.reference?.image?.url,
            };
          });
          
          allColors = allColors.concat(pageColors);
          
          // Check if there are more pages
          hasNextPage = result.data.metaobjects.pageInfo.hasNextPage;
          cursor = result.data.metaobjects.pageInfo.endCursor;
        }

        const customColors = allColors;

        // Cache the results globally with timestamp
        globalCustomColorsCache = {
          data: customColors,
          timestamp: Date.now()
        };
        
        return customColors;

      } catch (error) {
        console.error('❌ USF: Error fetching custom colors:', error);
        return [];
      } finally {
        // Clear the promise so subsequent calls can start fresh
        customColorsFetchPromise = null;
      }
    })();

    return await customColorsFetchPromise;
  }
  
  // Apply custom color mapping to USF product card swatches (optimized with caching)
  async function updateUSFProductCardSwatches() {
    // Prevent multiple simultaneous calls
    if (isProcessingSwatches) {
      // console.log('🔄 USF: Already processing swatches, skipping duplicate call');
      return;
    }
    
    isProcessingSwatches = true;
    
    try {
      // Quick check: are there any unmapped swatches that need processing?
      const allSwatches = document.querySelectorAll('.usf-product-swatch, .swatch[data-color], a[data-color], [data-color]');
      const unmappedSwatches = Array.from(allSwatches).filter(swatch => {
        const colorElement = swatch.querySelector('.color') || swatch.querySelector('span') || swatch;
        return colorElement && !colorElement.classList.contains('custom-color-enhanced') && 
               !colorElement.classList.contains('custom-color-image') && 
               !colorElement.classList.contains('custom-base-color');
      });
      
      if (unmappedSwatches.length === 0) {
        return;
      }
    
      // Use the optimized single-fetch function
      const customColors = await fetchCustomColorsOnce();
      
      if (!customColors || customColors.length === 0) {
        console.log('❌ USF: No custom colors available');
        return;
      }
      
      // Update all USF product card swatches
      const usfSwatches = document.querySelectorAll('.usf-product-swatch, .swatch[data-color], a[data-color], [data-color]');
    
      // Count unmapped swatches before processing
      const unmappedBefore = Array.from(usfSwatches).filter(swatch => {
        const colorElement = swatch.querySelector('.color') || swatch.querySelector('span') || swatch;
        return colorElement && !colorElement.classList.contains('custom-color-enhanced') && 
               !colorElement.classList.contains('custom-color-image') && 
               !colorElement.classList.contains('custom-base-color');
      }).length;
    
     
      // Collect all color names for debugging
      const colorNames = Array.from(usfSwatches).map(swatch => swatch.getAttribute('data-color')).filter(Boolean);
      const uniqueColorNames = [...new Set(colorNames)];
     
      
      usfSwatches.forEach((swatch, index) => {
        const colorName = swatch.getAttribute('data-color');
        const productId = swatch.getAttribute('data-product-id');
        const isCurrent = swatch.getAttribute('data-is-current') === 'true';
        
        if (!colorName) {
          
          return;
        }

        // Find the color span element inside the swatch - try multiple selectors
        let colorElement = swatch.querySelector('.color') || 
                          swatch.querySelector('span') || 
                          swatch.querySelector('.swatch-color') ||
                          swatch;
                          
        if (!colorElement) {
          
          return;
        }
        
        // console.log('🎨 USF: Processing swatch:', colorName, 'Element:', colorElement);

        // Find matching custom color (case-insensitive)
        let colorObj = customColors.find(c => 
          c.label && c.label.toLowerCase() === colorName.toLowerCase()
        );
        
        // If exact match not found, try partial matches
        if (!colorObj) {
          colorObj = customColors.find(c => 
            c.label && (
              c.label.toLowerCase().includes(colorName.toLowerCase()) ||
              colorName.toLowerCase().includes(c.label.toLowerCase())
            )
          );
          if (colorObj) {
            console.log(`🔍 USF: Partial match: ${colorName} → ${colorObj.label}`);
          }
        }

        let finalColor = null;

        if (colorObj) {
          // Apply custom styling based on available data
          if (colorObj.image_url) {
            colorElement.style.backgroundImage = `url(${colorObj.image_url})`;
            colorElement.style.backgroundSize = 'cover';
            colorElement.style.backgroundPosition = 'center';
            colorElement.style.backgroundColor = '';
            if (colorElement.style.removeProperty) {
              colorElement.style.removeProperty('--color');
            }
            colorElement.classList.add('custom-color-image');
            // For images, use a fallback color for border
            finalColor = colorObj.base_color || '#ccc';
          } else if (colorObj.base_color) {
            finalColor = colorObj.base_color;
            if (colorElement.style.setProperty) {
              colorElement.style.setProperty('--color', finalColor);
            }
            colorElement.style.backgroundColor = finalColor;
            colorElement.style.backgroundImage = '';
            colorElement.classList.add('custom-base-color', 'custom-color-enhanced');
          }
        } else {
          // Get the existing --color CSS variable or computed color
          const computedStyle = getComputedStyle(colorElement);
          finalColor = computedStyle.getPropertyValue('--color').trim() || 
                     computedStyle.backgroundColor;
        }

        // Apply USF-style active border with the swatch's actual color
        if (isCurrent && finalColor) {
          // Set the border color to match the swatch color
          if (colorElement.style.setProperty) {
            colorElement.style.setProperty('border-color', finalColor);
          }
          
          // Handle light colors that need darker borders
          const isLightColor = isColorLight(finalColor) || 
                              colorName.toLowerCase().includes('white') ||
                              colorName.toLowerCase().includes('cream') ||
                              colorName.toLowerCase().includes('ivory') ||
                              colorName.toLowerCase().includes('snow');
          
          if (isLightColor) {
            colorElement.style.borderColor = '#999';
            colorElement.style.boxShadow = '0 0 0 1px rgba(0, 0, 0, 0.2)';
          }
        }
      });
      
      // Count unmapped swatches after processing
      const unmappedAfter = Array.from(usfSwatches).filter(swatch => {
        const colorElement = swatch.querySelector('.color') || swatch.querySelector('span') || swatch;
        return colorElement && !colorElement.classList.contains('custom-color-enhanced') && 
               !colorElement.classList.contains('custom-color-image') && 
               !colorElement.classList.contains('custom-base-color');
      }).length;
     
    } catch (error) {
      // Silently handle errors to prevent console spam
      console.log('❌ USF: Error updating swatches:', error);
      return;
    } finally {
      isProcessingSwatches = false;
    }
  }

  // Helper function to determine if a color is light
  function isColorLight(color) {
    if (!color) return false;
    
    // Convert hex to RGB
    if (color.startsWith('#')) {
      const hex = color.slice(1);
      const r = parseInt(hex.substr(0, 2), 16);
      const g = parseInt(hex.substr(2, 2), 16);
      const b = parseInt(hex.substr(4, 2), 16);
      
      // Calculate relative luminance
      const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
      return luminance > 0.7;
    }
    
    // Handle rgb() format
    if (color.startsWith('rgb')) {
      const matches = color.match(/\d+/g);
      if (matches && matches.length >= 3) {
        const r = parseInt(matches[0]);
        const g = parseInt(matches[1]);
        const b = parseInt(matches[2]);
        const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
        return luminance > 0.7;
      }
    }
    
    return false;
  }

  // Prevent duplicate initialization
  if (window.usfSwatchInitialized) {
    
    return;
  }
  window.usfSwatchInitialized = true;

  // Initialize USF color swatches with retry mechanism for dynamic content
  if (typeof window.usfSwatchRetryCount === 'undefined') {
    window.usfSwatchRetryCount = 0;
  }
  if (typeof window.usfSwatchMaxRetries === 'undefined') {
    window.usfSwatchMaxRetries = 5;
  }
  if (typeof window.periodicCheckInterval === 'undefined') {
    window.periodicCheckInterval = null;
  }
  
  function initializeUSFSwatches() {
    if (window.usfSwatchRetryCount >= window.usfSwatchMaxRetries) return;
    
    updateUSFProductCardSwatches();
    
    // Retry for USF collection pages that load dynamically
    if (window.usfSwatchRetryCount < window.usfSwatchMaxRetries) {
      window.usfSwatchRetryCount++;
      setTimeout(initializeUSFSwatches, 1000 * window.usfSwatchRetryCount); // Increasing delay
    }
  }
  
  // Apply custom colors to specific swatches using cached data (no API calls)
  function applyCustomColorsToSwatches(swatches, customColors) {
    swatches.forEach((swatch, index) => {
      const colorName = swatch.getAttribute('data-color');
      const isCurrent = swatch.getAttribute('data-is-current') === 'true';
      
      if (!colorName) return;

      // Find the color span element inside the swatch
      let colorElement = swatch.querySelector('.color') || 
                        swatch.querySelector('span') || 
                        swatch.querySelector('.swatch-color') ||
                        swatch;
                        
      if (!colorElement) return;

      // Find matching custom color (case-insensitive)
      const colorObj = customColors.find(c => 
        c.label && c.label.toLowerCase() === colorName.toLowerCase()
      );

      let finalColor = null;

      if (colorObj) {
        // console.log(`🎨 USF: Applying color to ${colorName}:`, colorObj);
        
        // Apply custom styling based on available data
        if (colorObj.image_url) {
          colorElement.style.backgroundImage = `url(${colorObj.image_url})`;
          colorElement.style.backgroundSize = 'cover';
          colorElement.style.backgroundPosition = 'center';
          colorElement.style.backgroundColor = '';
          if (colorElement.style.removeProperty) {
            colorElement.style.removeProperty('--color');
          }
          colorElement.classList.add('custom-color-image');
          finalColor = colorObj.base_color || '#ccc';
          // console.log(`✅ USF: Applied image color to ${colorName}`);
        } else if (colorObj.base_color) {
          finalColor = colorObj.base_color;
          if (colorElement.style.setProperty) {
            colorElement.style.setProperty('--color', finalColor);
          }
          colorElement.style.backgroundColor = finalColor;
          colorElement.style.backgroundImage = '';
          colorElement.classList.add('custom-base-color', 'custom-color-enhanced');
          // console.log(`✅ USF: Applied base color ${finalColor} to ${colorName}`);
        }
      } else {
        // console.log(`⚠️ USF: No custom color found for ${colorName}, using existing color`);
        // Get the existing --color CSS variable or computed color
        const computedStyle = getComputedStyle(colorElement);
        finalColor = computedStyle.getPropertyValue('--color').trim() || 
                   computedStyle.backgroundColor;
      }

      // Apply USF-style active border with the swatch's actual color
      if (isCurrent && finalColor) {
        if (colorElement.style.setProperty) {
          colorElement.style.setProperty('border-color', finalColor);
        }
        
        // Handle light colors that need darker borders
        const isLightColor = isColorLight(finalColor) || 
                            colorName.toLowerCase().includes('white') ||
                            colorName.toLowerCase().includes('cream') ||
                            colorName.toLowerCase().includes('ivory') ||
                            colorName.toLowerCase().includes('snow');
        
        if (isLightColor) {
          colorElement.style.borderColor = '#999';
          colorElement.style.boxShadow = '0 0 0 1px rgba(0, 0, 0, 0.2)';
        }
      }
    });
    
    
  }

  // More aggressive function that checks all swatches on the page
  function reapplyAllSwatches() {
    
    updateUSFProductCardSwatches();
  }
  
  // Track processed swatches to avoid infinite loops
  if (typeof window.processedSwatchIds === 'undefined') {
    window.processedSwatchIds = new Set();
  }
  
  // Start periodic check for missed swatches (every 5 seconds, optimized)
  function startPeriodicSwatchCheck() {
    if (window.periodicCheckInterval) return; // Already running
    
    window.periodicCheckInterval = setInterval(() => {
      // Only check if we have custom colors cached
      if (!globalCustomColorsCache || globalCustomColorsCache.length === 0) {
        return; // Skip if no colors available
      }
      
      const allSwatches = document.querySelectorAll('.usf-product-swatch, .swatch[data-color], a[data-color], [data-color]');
      const unmappedSwatches = Array.from(allSwatches).filter(swatch => {
        const colorElement = swatch.querySelector('.color') || swatch.querySelector('span') || swatch;
        const hasCustomClass = colorElement && (
          colorElement.classList.contains('custom-color-enhanced') || 
          colorElement.classList.contains('custom-color-image') || 
          colorElement.classList.contains('custom-base-color')
        );
        
        // Create a unique ID for this swatch
        const swatchId = swatch.getAttribute('data-color') + '_' + 
                        (swatch.closest('[data-product-id]')?.getAttribute('data-product-id') || 'unknown');
        
        // Skip if already processed and has custom styling
        if (processedSwatchIds.has(swatchId) && hasCustomClass) {
          return false;
        }
        
        return !hasCustomClass;
      });
      
      if (unmappedSwatches.length > 0) {
        // console.log(`🔍 USF: Found ${unmappedSwatches.length} unmapped swatches, applying cached colors...`);
        // Apply colors using cached data without API call
        // Extract array from cache object if needed
        const colorsArray = globalCustomColorsCache?.data || globalCustomColorsCache;
        if (colorsArray && Array.isArray(colorsArray)) {
          applyCustomColorsToSwatches(unmappedSwatches, colorsArray);
          
          // Mark swatches as processed
          unmappedSwatches.forEach(swatch => {
            const swatchId = swatch.getAttribute('data-color') + '_' + 
                            (swatch.closest('[data-product-id]')?.getAttribute('data-product-id') || 'unknown');
            window.processedSwatchIds.add(swatchId);
          });
          
        } else {
          console.log('⚠️ USF: Cache is not ready or not an array');
        }
      } else {
        // console.log('✅ USF: All swatches are properly mapped');
      }
    }, 10000); // Check every 10 seconds (much less aggressive)
    
    // console.log('🔄 USF: Started optimized periodic swatch check');
  }
  
  // Stop periodic check
  function stopPeriodicSwatchCheck() {
    if (periodicCheckInterval) {
      clearInterval(periodicCheckInterval);
      periodicCheckInterval = null;
      // console.log('🔄 USF: Stopped periodic swatch check');
    }
  }

  // Run after DOM is fully loaded
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function() {
      initializeUSFSwatches();
      startPeriodicSwatchCheck();
    });
  } else {
    // DOM already loaded
    setTimeout(initializeUSFSwatches, 100);
    setTimeout(startPeriodicSwatchCheck, 1000);
  }
  
  // Also run when USF updates content (debounced to reduce calls)
  if (typeof window.usfUpdateTimeout === 'undefined') {
    window.usfUpdateTimeout = null;
  }
  if (window.usf && window.usf.on) {
    window.usf.on('resultsUpdated', function() {
      // console.log('🔍 USF: Results updated, debouncing swatch re-initialization');
      clearTimeout(window.usfUpdateTimeout);
      window.usfUpdateTimeout = setTimeout(initializeUSFSwatches, 1000); // Increased delay
    });
  }
  
  // Listen for DOM mutations to catch dynamically added swatches (debounced)
  if (typeof window.mutationTimeout === 'undefined') {
    window.mutationTimeout = null;
  }
  if (typeof MutationObserver !== 'undefined') {
    const usfSwatchObserver = new MutationObserver(function(mutations) {
      let shouldUpdate = false;
      mutations.forEach(function(mutation) {
        if (mutation.type === 'childList') {
          mutation.addedNodes.forEach(function(node) {
            if (node.nodeType === 1) {
              // Check if the node itself is a swatch
              if (node.matches && (
                node.matches('[data-color]') || 
                node.matches('.usf-product-swatch') ||
                node.matches('.swatch[data-color]')
              )) {
                shouldUpdate = true;
              }
              // Check if the node contains swatches
              else if (node.querySelector && (
                node.querySelector('[data-color]') || 
                node.querySelector('.usf-product-swatch') ||
                node.querySelector('.swatch[data-color]') ||
                node.querySelector('.product-card') ||
                node.querySelector('.usf-product')
              )) {
                shouldUpdate = true;
              }
            }
          });
        }
      });
      
      if (shouldUpdate) {
        // console.log('🔍 USF: New swatches detected in DOM, debouncing update...');
        clearTimeout(mutationTimeout);
        mutationTimeout = setTimeout(initializeUSFSwatches, 800); // Increased delay
      }
    });
    
    // Only observe if document.body exists
    if (document.body) {
      usfSwatchObserver.observe(document.body, {
        childList: true,
        subtree: true
      });
    }
  }
  
  // Also listen for USF-specific load more events
  if (window.usf && window.usf.on) {
    // Listen for when USF loads more products
    window.usf.on('resultsUpdated', function() {
      
      clearProcessedSwatches(); // Clear tracking for new content
      setTimeout(initializeUSFSwatches, 500);
    });
    
    // Listen for when USF finishes loading
    window.usf.on('resultsLoaded', function() {
      
      clearProcessedSwatches(); // Clear tracking for new content
      setTimeout(initializeUSFSwatches, 300);
    });
    
    // Listen for when USF updates the product grid
    window.usf.on('gridUpdated', function() {
      
      clearProcessedSwatches(); // Clear tracking for new content
      setTimeout(initializeUSFSwatches, 200);
    });
  }
  
  // Monitor for "Load more" button clicks
  document.addEventListener('click', function(event) {
    const target = event.target;
    const buttonText = target.textContent?.toLowerCase() || '';
    
    // Check if it's a load more button
    if (buttonText.includes('load more') || 
        buttonText.includes('show more') || 
        target.classList.contains('load-more') ||
        target.closest('.load-more') ||
        target.closest('[data-load-more]')) {
      
      // Wait a bit for the new content to load, then re-initialize
      setTimeout(initializeUSFSwatches, 1000);
    }
  });
  
  // Monitor scroll events for infinite scroll scenarios
  if (typeof window.scrollTimeout === 'undefined') {
    window.scrollTimeout = null;
  }
  window.addEventListener('scroll', function() {
    // Debounce scroll events
    clearTimeout(window.scrollTimeout);
    window.scrollTimeout = setTimeout(() => {
      // Check if we're near the bottom of the page (infinite scroll)
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      const windowHeight = window.innerHeight;
      const documentHeight = document.documentElement.scrollHeight;
      
      if (scrollTop + windowHeight >= documentHeight - 500) { // 500px from bottom
        // console.log('🔍 USF: Near bottom of page, checking for new swatches...');
        // Use cached colors if available, otherwise trigger full update
        const colorsArray = globalCustomColorsCache?.data || globalCustomColorsCache;
        if (colorsArray && Array.isArray(colorsArray) && colorsArray.length > 0) {
          const allSwatches = document.querySelectorAll('.usf-product-swatch, .swatch[data-color], a[data-color], [data-color]');
          const unmappedSwatches = Array.from(allSwatches).filter(swatch => {
            const colorElement = swatch.querySelector('.color') || swatch.querySelector('span') || swatch;
            return colorElement && !colorElement.classList.contains('custom-color-enhanced') && 
                   !colorElement.classList.contains('custom-color-image') && 
                   !colorElement.classList.contains('custom-base-color');
          });
          
          if (unmappedSwatches.length > 0) {
            // console.log(`🔍 USF: Found ${unmappedSwatches.length} unmapped swatches on scroll, applying cached colors...`);
            applyCustomColorsToSwatches(unmappedSwatches, colorsArray);
          }
        } else {
          reapplyAllSwatches();
        }
      }
    }, 500); // Wait 500ms after scroll stops
  });
  
  // Clear processed swatches when new content is loaded
  function clearProcessedSwatches() {
    window.processedSwatchIds.clear();
    // console.log('🔄 USF: Cleared processed swatches tracking');
  }

  // Expose functions globally for manual triggering
  window.reinitializeUSFSwatches = initializeUSFSwatches;
  window.reapplyAllUSFSwatches = reapplyAllSwatches;
  window.startUSFPeriodicCheck = startPeriodicSwatchCheck;
  window.stopUSFPeriodicCheck = stopPeriodicSwatchCheck;
  window.getUSFCustomColorsCache = () => globalCustomColorsCache;
  window.clearUSFCustomColorsCache = () => {
    globalCustomColorsCache = null;
    // console.log('🔄 USF: Custom colors cache cleared');
  };
  window.clearUSFProcessedSwatches = clearProcessedSwatches;
  window.forceUSFFallbackColors = async () => {
    
    globalCustomColorsCache = null; // Clear cache to force re-fetch
    const colors = await fetchCustomColorsOnce();
    if (colors && colors.length > 0) {
      
      updateUSFProductCardSwatches();
      return colors.length;
    }
    return 0;
  };
  window.applyUSFCachedColors = () => {
    const colorsArray = globalCustomColorsCache?.data || globalCustomColorsCache;
    if (colorsArray && Array.isArray(colorsArray) && colorsArray.length > 0) {
      const allSwatches = document.querySelectorAll('.usf-product-swatch, .swatch[data-color], a[data-color], [data-color]');
      const unmappedSwatches = Array.from(allSwatches).filter(swatch => {
        const colorElement = swatch.querySelector('.color') || swatch.querySelector('span') || swatch;
        return colorElement && !colorElement.classList.contains('custom-color-enhanced') && 
               !colorElement.classList.contains('custom-color-image') && 
               !colorElement.classList.contains('custom-base-color');
      });
      applyCustomColorsToSwatches(unmappedSwatches, colorsArray);
      return unmappedSwatches.length;
    }
    return 0;
  };

  // Add USF-specific CSS styles
  const usfSwatchStyles = `
    <style>
    /* USF Product Card Swatch Styles */
    .usf-product-swatch.current-swatch .color,
    .usf-enhanced-swatches .swatch.current-swatch .color {
      border-color: var(--color, #000) !important;
      box-shadow: 0 0 0 1px rgba(0, 0, 0, 0.1) !important;
    }
    
    /* Custom color image styling for USF swatches */
    .usf-product-swatch .color.custom-color-image {
      background-color: transparent !important;
      background-size: cover !important;
      background-position: center !important;
    }
    
    /* Custom base color styling for USF swatches */  
    .usf-product-swatch .color.custom-base-color {
      background-color: var(--color) !important;
    }
    
    /* Enhanced custom color styling */
    .usf-product-swatch .color.custom-color-enhanced {
      /*background-color: var(--color) !important;*/  
      border-radius: 50% !important;
    }
    
    /* Ensure custom colors override default styling */
    .usf-product-swatch .color.custom-color-image,
    .usf-product-swatch .color.custom-base-color,
    .usf-product-swatch .color.custom-color-enhanced {
      border-radius: 50% !important;
    }
    
    /* White/light colors need darker border */
    .usf-product-swatch[data-color*="white"] .color,
    .usf-product-swatch[data-color*="cream"] .color,
    .usf-product-swatch[data-color*="ivory"] .color,
    .usf-product-swatch[data-color*="snow"] .color {
      border-color: #ccc !important;
    }
    
    .usf-product-swatch[data-color*="white"].current-swatch .color,
    .usf-product-swatch[data-color*="cream"].current-swatch .color,
    .usf-product-swatch[data-color*="ivory"].current-swatch .color,
    .usf-product-swatch[data-color*="snow"].current-swatch .color {
      border-color: #999 !important;
      box-shadow: 0 0 0 1px rgba(0, 0, 0, 0.2) !important;
    }
    </style>
  `;
  
  // Inject the styles
  document.head.insertAdjacentHTML('beforeend', usfSwatchStyles);

  // Add USF custom badge styles to ensure colors are applied
  const usfBadgeStyles = `
    <style id="usf-custom-badge-styles">
    /* USF Custom Badge Styles - Ensure badge colors are applied correctly */
    ${window._usfGlobalSettings && window._usfGlobalSettings.custom_badges ? 
      window._usfGlobalSettings.custom_badges.split(';').map(badge => {
        const badgeStr = badge.trim();
        if (!badgeStr) return '';
        
        const parts = badgeStr.split(':');
        const badgeName = parts[0];
        const badgeClass = badgeName.toLowerCase().replace(/[^a-z0-9]+/g, '-');
        let bgColor, fgColor;
        
        if (parts.length >= 2) {
          const colorPart = parts[1];
          
          if (colorPart && colorPart.includes(',')) {
            // Format: [Product tag]:[Background color],[Text color]
            const colors = colorPart.split(',').map(c => c.trim());
            bgColor = colors[0];
            fgColor = colors[1];
          } else if (parts.length >= 3) {
            // Format: [Product tag]:[Background color]:[Text color]
            bgColor = parts[1].trim();
            fgColor = parts[2].trim();
          }
        }
        
        if (!bgColor || !fgColor) {
          console.warn('⚠️ Invalid badge configuration:', badgeStr);
          return '';
        }
        
        
        
        return `.usf-sr-product .badge.custom-${badgeClass} {
          color: ${fgColor} !important;
          background-color: ${bgColor} !important;
        }`;
      }).filter(s => s).join('\n') : ''}
    </style>
  `;
  
  // Only inject if custom badges are defined
  if (window._usfGlobalSettings && window._usfGlobalSettings.custom_badges) {
    if (!document.getElementById('usf-custom-badge-styles')) {
      document.head.insertAdjacentHTML('beforeend', usfBadgeStyles);
      
    }
  }

  // Generic click listener for load more buttons (debounced)
  if (typeof window.clickTimeout === 'undefined') {
    window.clickTimeout = null;
  }
  document.addEventListener('click', function(event) {
    const target = event.target;
    if (!target) return;
    
    const buttonText = (target.textContent || '').toLowerCase();
    
    // Handle className - it can be a string, DOMTokenList, or SVGAnimatedString
    let buttonClass = '';
    try {
      if (target.className) {
        if (typeof target.className === 'string') {
          buttonClass = target.className.toLowerCase();
        } else if (target.className.baseVal !== undefined) {
          // SVG element with SVGAnimatedString
          buttonClass = String(target.className.baseVal || '').toLowerCase();
        } else if (target.className.toString) {
          // DOMTokenList or other object with toString
          buttonClass = String(target.className).toLowerCase();
        }
      }
    } catch (e) {
      // If className access fails, just use empty string
      buttonClass = '';
    }
    
    if (buttonText.includes('load more') || buttonText.includes('show more') || 
        buttonClass.includes('load-more') || buttonClass.includes('show-more')) {
      // console.log('🔍 USF: Load more button clicked, debouncing swatch re-initialization');
      clearTimeout(window.clickTimeout);
      clearProcessedSwatches();
      window.clickTimeout = setTimeout(initializeUSFSwatches, 1500); // Increased delay
    }
  });

  // ========================================
  // USF Badge Display Logic Override
  // ========================================
  
  // Override USF badge display logic with new priority rules
  function overrideUSFBadgeLogic() {
    if (!window.usf || !window.usf.templates) {
      
      return false;
    }
    
    // Override the searchResultsGridViewItem template directly
    if (window.usf.templates.searchResultsGridViewItem) {
      const originalTemplate = window.usf.templates.searchResultsGridViewItem;
      
      
      
      // Replace the badge logic in the template
      const updatedTemplate = originalTemplate.replace(
        /<div v-if="\(hasDiscount && usf\.settings\.search\.showSale\) \|\| product\.tags\.includes\('preorder'\) \|\| product\.tags\.includes\('new'\) \|\| \(isSoldOut && usf\.settings\.search\.showSoldOut\) \|\| \(customBadge && customBadge\.length\)" class="badges">[\s\S]*?<\/div>/,
        `<div v-if="(hasDiscount && usf.settings.search.showSale) || product.tags.includes('preorder') || product.tags.includes('new') || (isSoldOut && usf.settings.search.showSoldOut) || (customBadge && customBadge.length)" class="badges">
            <!-- New Badge Logic:
            - Sale + Custom + Default → Display "Sale + Custom"
            - Sale + Custom → Display "Sale + Custom"  
            - Sale + Default → Display "Sale + Default"
            - Custom + Default → Display "Custom + Default"
            
            Note: customBadge array contains ALL badges including custom tags and default badges (padding level, underwear features)
            We need to separate actual custom tag badges from default metafield badges
            -->
            
            <!-- Sale Badge (highest priority) -->
            <span v-if="hasDiscount && usf.settings.search.showSale" class="badge sale" :style="'--text-color:' + _usfGlobalSettings.badge_sale_foreground + ';--background-color:' + _usfGlobalSettings.badge_sale_background + ';'" v-html="loc.sale"></span>
            
            <!-- Custom Badges Loop - Show first custom tag badge (NOT default metafield badges) -->
            <template v-if="customBadge && customBadge.length > 0">
              <template v-for="(badge, index) in customBadge">
                <!-- Only show the first custom tag badge (has text_color/background_color from settings) -->
                <!-- Default badges have hardcoded #2b2b2b background -->
                <!-- Use ONLY CSS class-based styling like product pages (no inline styles) -->
                <span v-if="index === 0 && badge.background_color && !badge.background_color.includes('#2b2b2b')" 
                      :class="'badge custom-' + badge.badge.toLowerCase().replace(/[^a-z0-9]+/g, '-')" 
                      v-html="badge.badge"></span>
              </template>
            </template>
            
            <!-- Default Badges (only show when NOT both sale AND custom tag badge) -->
            <template v-if="!(hasDiscount && usf.settings.search.showSale && customBadge && customBadge.some(b => b.background_color !== '#2b2b2b'))">
              <template v-if="customBadge && customBadge.length > 0">
                <template v-for="badge in customBadge">
                  <!-- Show default badges (padding level, underwear features) with dark background -->
                  <span v-if="badge.background_color === '#2b2b2b'" 
                        class="badge feature" 
                        style="--text-color: #ffffff; --background-color: #2b2b2b;" 
                        v-html="badge.badge"></span>
                </template>
              </template>
              <!-- Legacy badges (New, Preorder, Soldout) - only if no default metafield badges -->
              <template v-if="!customBadge || !customBadge.some(b => b.background_color === '#2b2b2b')">
                <span v-if="product.tags.includes('preorder')" class="badge preorder" :style="'--text-color:' +  _usfGlobalSettings.badge_preorder_foreground  + ';--background-color:' + _usfGlobalSettings.badge_preorder_background +';'" v-html="_usfPreorderTxt"></span>
                <span v-if="product.tags.includes('new')" class="badge new" :style="'--text-color:' + _usfGlobalSettings.badge_new_foreground + ';--background-color:' + _usfGlobalSettings.badge_new_background + ';'" v-html="_usfNewTxt"></span>
                <span v-if="isSoldOut && usf.settings.search.showSoldOut" class="badge soldout" :style="'--text-color:' + _usfGlobalSettings.badge_sold_out_foreground + ';--background-color:' + _usfGlobalSettings.badge_sold_out_background + ';'" v-html="loc.soldOut"></span>
              </template>
            </template>
        </div>`
      );
      
      // Check if the replacement actually happened
      if (updatedTemplate !== originalTemplate) {
        window.usf.templates.searchResultsGridViewItem = updatedTemplate;
        
        return true;
      } else {
        
        return false;
      }
    } else {
      
      return false;
    }
  }
  
  // Apply badge logic override when USF is ready
  // Use window properties to avoid redeclaration errors
  if (typeof window.badgeOverrideAttempts === 'undefined') {
    window.badgeOverrideAttempts = 0;
  }
  if (typeof window.maxBadgeOverrideAttempts === 'undefined') {
    window.maxBadgeOverrideAttempts = 20;
  }
  
  function applyUSFBadgeOverride() {
    window.badgeOverrideAttempts++;
    
    if (overrideUSFBadgeLogic()) {
      console.log('✅ Badge override completed successfully');
      return;
    }
    
    if (window.badgeOverrideAttempts < window.maxBadgeOverrideAttempts) {
      console.log(`🔄 Retry ${window.badgeOverrideAttempts}/${window.maxBadgeOverrideAttempts}...`);
      setTimeout(applyUSFBadgeOverride, 200);
    } else {
      console.log('❌ Failed to override USF badge logic after maximum attempts');
    }
  }
  
  // Initialize badge override - try immediately and on DOM ready
  // Only initialize once, and ONLY on collection/search pages (not product or cart pages)
  if (!window.usfBadgeOverrideInitialized) {
    window.usfBadgeOverrideInitialized = true;
    
    // Skip on product pages and cart pages - USF is only used on collection/search pages
    if (shouldRunUSFOnPage()) {
      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', applyUSFBadgeOverride);
      } else {
        applyUSFBadgeOverride();
      }
    }
  }
  
  // Expose badge override function globally for debugging
  window.overrideUSFBadgeLogic = overrideUSFBadgeLogic;

  // Sale Only checkbox functionality
  async function initializeSaleOnlyCheckbox() {
    console.log('🔥 Initializing Sale Only checkbox...');
    
    // Wait for USF to be ready
    try {
      await waitFor(
        () => typeof usf !== 'undefined' && usf.event,
        { timeout: 10000, description: 'USF events' }
      );
      
      // Add the checkbox after USF is mounted
      usf.event.add('mounted', addSaleOnlyCheckbox);
      
      // Also add it when search results are updated
      usf.event.add('sr_updated', async function() {
        if (!document.querySelector('.usf-sale-only-checkbox')) {
          await nextFrame();
          addSaleOnlyCheckbox();
        }
      });
    } catch (error) {
      console.log('⚠️ USF events not ready for Sale Only checkbox');
    }
  }
  
  function addSaleOnlyCheckbox() {
    // Find the appropriate container for the checkbox
    const filterContainer = document.querySelector('.usf-sr-config') || 
                          document.querySelector('.usf-facets') || 
                          document.querySelector('.usf-sr-top');
    
    if (!filterContainer || document.querySelector('.usf-sale-only-checkbox')) {
      return;
    }
    
    // Create the checkbox container
    // Add custom styles for the checkbox
    const style = document.createElement('style');
    style.textContent = `
      .usf-sale-only-checkbox {
        margin-bottom: 8px;
        display: none !important;
      }
      .usf-sale-only-label {
        display: flex !important;
        align-items: center !important;
        gap: 8px !important;
        padding: 12px !important;
        cursor: pointer !important;
        user-select: none !important;
        color: var(--color-card-foreground) !important;
        transition: background-color 0.2s !important;
        margin: 0 !important;
        text-transform: uppercase;
        font-size: 0.9em;
      }
     
      #usf-sale-only-input {
        margin: 0 !important;
        cursor: pointer !important;
        width: 18px !important;
        height: 18px !important;
        accent-color: var(--color-card-foreground) !important;
        flex-shrink: 0 !important;
        border-radius: 0;
      }
      #usf-sale-only-input:checked {
        background-color: var(--color-card-foreground) !important;
        border-color: var(--color-card-foreground) !important;
      }
     
    `;
    document.head.appendChild(style);
    
    const checkboxContainer = document.createElement('div');
    checkboxContainer.className = 'usf-sale-only-checkbox';
    checkboxContainer.innerHTML = `
      <label class="usf-sale-only-label" for="usf-sale-only-input">
        <input type="checkbox" id="usf-sale-only-input">
        <span>Sale Only</span>
      </label>
    `;
    
    // Insert the checkbox at the top of the filter container
    filterContainer.insertBefore(checkboxContainer, filterContainer.firstChild);
    
    // Add event listener
    const checkbox = checkboxContainer.querySelector('#usf-sale-only-input');
    checkbox.addEventListener('change', handleSaleOnlyChange);
    
    // Monitor USF filter changes to sync checkbox state
    if (usf && usf.event) {
      // Listen to multiple events to catch all filter changes
      usf.event.add('filter.changed', function() {
        console.log('📡 Filter changed - syncing Sale Only state');
        syncSaleOnlyCheckboxState();
      });
      
      usf.event.add('sr_updated', function() {
        console.log('📡 Search results updated - syncing Sale Only state');
        syncSaleOnlyCheckboxState();
      });
      
      usf.event.add('filter.removed', function() {
        console.log('📡 Filter removed - syncing Sale Only state');
        syncSaleOnlyCheckboxState();
      });
    }
    
    // Check initial state on page load - if all sale filters are already selected, check the box
    // Wait for filters to render before syncing
    (async () => {
      try {
        await waitForElement('.usf-facet', { timeout: 5000 });
        await nextFrame();
        syncSaleOnlyCheckboxState();
        console.log('🔍 Checked initial Sale Only state on page load');
      } catch (error) {
        console.log('⚠️ Filters not rendered yet, will sync on next update');
      }
    })();
    
    console.log('✅ Sale Only checkbox added successfully');
  }
  
  function syncSaleOnlyCheckboxState() {
    const checkbox = document.querySelector('#usf-sale-only-input');
    if (!checkbox) {
      console.log('⚠️ Sale Only checkbox not found for sync');
      return;
    }
    
    // Check if any sale filters are currently active
    const percentOffSection = Array.from(document.querySelectorAll('.usf-facet')).find(facet => {
      const title = facet.querySelector('.usf-title, .usf-label');
      return title && (
        title.textContent.toLowerCase().includes('off') ||
        title.textContent.toLowerCase().includes('%') ||
        title.textContent.toLowerCase().includes('percent')
      );
    });
    
    if (!percentOffSection) {
      checkbox.checked = false;
      console.log('⚠️ % OFF section not found - unchecking Sale Only');
      return;
    }
    
    // Check if all sale options are selected (excluding Non-sale)
    const filterOptions = percentOffSection.querySelectorAll('.usf-facet-value');
    let saleOptionsCount = 0;
    let selectedSaleOptionsCount = 0;
    
    filterOptions.forEach(option => {
      const labelElement = option.querySelector('.usf-label') || 
                          option.querySelector('label') || 
                          option.querySelector('span') ||
                          option;
      
      const labelText = (labelElement.textContent || labelElement.innerText || '').toLowerCase().trim();
      
      // Skip non-sale options
      if (!labelText || 
          labelText.includes('non-sale') || 
          labelText.includes('no sale') ||
          labelText.includes('non sale')) {
        return;
      }
      
      saleOptionsCount++;
      
      const checkbox = option.querySelector('input[type="checkbox"]');
      const isSelected = option.classList.contains('usf-selected') || 
                        (checkbox && checkbox.checked);
      
      if (isSelected) {
        selectedSaleOptionsCount++;
      }
    });
    
    // If all sale options are selected, check the Sale Only checkbox
    const allSaleOptionsSelected = saleOptionsCount > 0 && saleOptionsCount === selectedSaleOptionsCount;
    const saleOnlyCheckbox = document.querySelector('#usf-sale-only-input');
    const checkboxContainer = document.querySelector('.usf-sale-only-checkbox');
    
    console.log('🔍 Sync state:', {
      saleOptionsCount,
      selectedSaleOptionsCount,
      allSaleOptionsSelected
    });
    
    if (saleOnlyCheckbox) {
      saleOnlyCheckbox.checked = allSaleOptionsSelected;
      if (allSaleOptionsSelected) {
        saleOnlyCheckbox.setAttribute('checked', 'checked');
        if (checkboxContainer) {
          checkboxContainer.classList.add('is-checked');
        }
        console.log('✅ Sale Only checkbox CHECKED - all sale filters selected');
      } else {
        saleOnlyCheckbox.removeAttribute('checked');
        if (checkboxContainer) {
          checkboxContainer.classList.remove('is-checked');
        }
        console.log('❌ Sale Only checkbox UNCHECKED - not all sale filters selected');
      }
    }
  }
  
  function handleSaleOnlyChange(event) {
    const isChecked = event.target.checked;
    console.log('🔥 Sale Only checkbox changed:', isChecked);
    
    if (isChecked) {
      applySaleOnlyFilter();
      // Keep checkbox checked
      event.target.checked = true;
    } else {
      removeSaleOnlyFilter();
      // Keep checkbox unchecked
      event.target.checked = false;
    }
  }
  
  function applySaleOnlyFilter() {
    console.log('🔥 Applying Sale Only filter...');
    
    // Keep the checkbox checked immediately
    const saleOnlyCheckbox = document.querySelector('#usf-sale-only-input');
    const checkboxContainer = document.querySelector('.usf-sale-only-checkbox');
    if (saleOnlyCheckbox) {
      saleOnlyCheckbox.checked = true;
      saleOnlyCheckbox.setAttribute('checked', 'checked');
      if (checkboxContainer) {
        checkboxContainer.classList.add('is-checked');
      }
      console.log('✅ Sale Only checkbox set to checked');
    }
    
    try {
      // Strategy: Find and click the actual filter checkboxes in the DOM
      // This is the most reliable way as it uses USF's built-in UI logic
      
      // Find the % OFF filter section
      const percentOffSection = Array.from(document.querySelectorAll('.usf-facet')).find(facet => {
        const title = facet.querySelector('.usf-title, .usf-label');
        return title && (
          title.textContent.toLowerCase().includes('off') ||
          title.textContent.toLowerCase().includes('%') ||
          title.textContent.toLowerCase().includes('percent')
        );
      });
      
      if (!percentOffSection) {
        console.log('⚠️ Could not find % OFF filter section in DOM');
        applySaleOnlyFilterFallback();
        return;
      }
      
      console.log('✅ Found % OFF filter section');
      
      // Find all filter option checkboxes within this section
      const filterOptions = percentOffSection.querySelectorAll('.usf-facet-value');
      console.log('Found filter options:', filterOptions.length);
      
      if (filterOptions.length === 0) {
        console.log('⚠️ No filter options found');
        applySaleOnlyFilterFallback();
        return;
      }
      
      let clickedCount = 0;
      
      // Click each sale option (exclude "Non-sale")
      filterOptions.forEach((option, index) => {
        // Debug: log the whole option element
        console.log(`Option ${index} element:`, option);
        
        // Try multiple selectors to find the label text
        const labelElement = option.querySelector('.usf-label') || 
                            option.querySelector('label') || 
                            option.querySelector('span') ||
                            option;
        
        const labelText = (labelElement.textContent || labelElement.innerText || '').toLowerCase().trim();
        
        console.log(`  Label text: "${labelText}"`);
        
        // Skip non-sale options - be more specific with checks
        if (!labelText || 
            labelText.includes('non-sale') || 
            labelText.includes('no sale') ||
            labelText.includes('non sale') ||
            labelText === '0%' ||
            labelText === '0') {
          console.log(`  → Skipping non-sale option`);
          return;
        }
        
        // Find the checkbox or clickable element
        const checkbox = option.querySelector('input[type="checkbox"]');
        const clickableElement = checkbox || option;
        
        // Check if it's already selected
        const isSelected = option.classList.contains('usf-selected') || 
                          (checkbox && checkbox.checked);
        
        if (!isSelected) {
          console.log(`  → Will click: "${labelText}"`);
          // Store for batch clicking
          clickableElement.click();
          clickedCount++;
        } else {
          console.log(`  → Already selected: "${labelText}"`);
        }
      });
      
      console.log(`✅ Applied Sale Only filter by clicking ${clickedCount} options`);
      
      // Small delay to let USF process all the clicks together
      if (clickedCount > 0) {
        console.log('⏳ Waiting for USF to process filters...');
        
        // Keep the checkbox checked even after USF updates (use RAF for better timing)
        (async () => {
          await nextFrame();
          const checkbox = document.querySelector('#usf-sale-only-input');
          const container = document.querySelector('.usf-sale-only-checkbox');
          if (checkbox) {
            checkbox.checked = true;
            checkbox.setAttribute('checked', 'checked');
            if (container) {
              container.classList.add('is-checked');
            }
            console.log('🔒 Re-checked Sale Only checkbox after filter application');
          }
          
          // Check again after USF fully processes
          await delay(400);
          if (checkbox) {
            checkbox.checked = true;
            checkbox.setAttribute('checked', 'checked');
            if (container) {
              container.classList.add('is-checked');
            }
          }
        })();
      }
      
      if (clickedCount === 0) {
        console.log('⚠️ No options were clicked, using fallback');
        applySaleOnlyFilterFallback();
      }
      
    } catch (error) {
      console.error('❌ Error in applySaleOnlyFilter:', error);
      applySaleOnlyFilterFallback();
    }
  }
  
  async function applySaleOnlyFilterFallback() {
    console.log('🔥 Applying Sale Only filter (fallback method)...');
    
    // This is a fallback method that works by filtering the displayed results
    // after they're loaded, rather than at the API level
    await delay(500);
    await nextFrame();
      const productItems = document.querySelectorAll('.usf-sr-product, [data-usf-pid]');
      let hiddenCount = 0;
      
      productItems.forEach(item => {
        let hasDiscount = false;
        
        // Look for discount indicators in multiple ways
        const saleIndicators = [
          '.usf-discount',
          '.usf-sale',
          '.product-discount',
          '.sale-badge',
          '.discount-badge',
          '[class*="sale"]',
          '[class*="discount"]'
        ];
        
        // Check if item has any sale indicators
        for (const selector of saleIndicators) {
          if (item.querySelector(selector)) {
            hasDiscount = true;
            break;
          }
        }
        
        // Also check for price comparison
        if (!hasDiscount) {
          const priceElement = item.querySelector('.usf-price, .price');
          const comparePriceElement = item.querySelector('.usf-compare-price, .usf-original-price, .compare-price, .original-price');
          
          if (priceElement && comparePriceElement) {
            const priceText = priceElement.textContent.trim();
            const comparePriceText = comparePriceElement.textContent.trim();
            
            // Check if prices are different (indicating a sale)
            if (priceText !== comparePriceText && comparePriceText !== '') {
              hasDiscount = true;
            }
          }
        }
        
        // Check for percentage off text
        if (!hasDiscount) {
          const itemText = item.textContent.toLowerCase();
          if (itemText.includes('%') && itemText.includes('off')) {
            hasDiscount = true;
          }
        }
        
        if (!hasDiscount) {
          item.style.display = 'none';
          item.classList.add('usf-hidden-by-sale-filter');
          hiddenCount++;
        } else {
          item.style.display = '';
          item.classList.remove('usf-hidden-by-sale-filter');
        }
      });
      
      console.log(`✅ Sale Only filter applied (fallback). Hidden ${hiddenCount} non-sale items.`);
      
      // Update results count if visible
      updateResultsCount();
  }
  
  function removeSaleOnlyFilter() {
    console.log('🔥 Removing Sale Only filter...');
    
    // Remove from facet filters using USF store
    const facetsStore = usf?.store?.useFacetsStore && usf.store.useFacetsStore();
    
    if (facetsStore) {
      const facets = facetsStore.facets;
      
      if (facets && facets.length > 0) {
        // Find the % OFF facet
        const percentOffFacet = facets.find(facet => 
          facet.title && (
            facet.title.toLowerCase().includes('off') ||
            facet.title.toLowerCase().includes('%') ||
            facet.title.toLowerCase().includes('percent') ||
            facet.title.toLowerCase().includes('discount') ||
            facet.title.toLowerCase().includes('sale')
          )
        );
        
        if (percentOffFacet) {
          // Remove the filter
          facetsStore.removeFacetFilter(percentOffFacet.id, null);
          
          console.log('✅ Removed sale filter from facets');
          
          // Refresh the search results
          if (usf.search && usf.search.refresh) {
            usf.search.refresh();
          }
        }
      }
    }
    
    // Also remove any fallback hiding
    (async () => {
      await nextFrame();
      const hiddenItems = document.querySelectorAll('.usf-hidden-by-sale-filter');
      hiddenItems.forEach(item => {
        item.style.display = '';
        item.classList.remove('usf-hidden-by-sale-filter');
      });
      
      if (hiddenItems.length > 0) {
        console.log(`✅ Restored ${hiddenItems.length} hidden items`);
      }
      
      // Update results count
      updateResultsCount();
    })();
  }
  
  function updateResultsCount() {
    // Update the results count display if it exists
    const resultsCountElement = document.querySelector('.usf-sr-summary, .usf-results-count');
    if (resultsCountElement) {
      const visibleProducts = document.querySelectorAll('.usf-sr-product:not(.usf-hidden-by-sale-filter), [data-usf-pid]:not(.usf-hidden-by-sale-filter)');
      const count = visibleProducts.length;
      
      // Update the count in the text
      const currentText = resultsCountElement.textContent;
      const newText = currentText.replace(/\d+/, count);
      resultsCountElement.textContent = newText;
    }
  }
  
  // Initialize the Sale Only checkbox - skip on cart and product pages
  if (shouldRunUSFOnPage()) {
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initializeSaleOnlyCheckbox);
  } else {
    initializeSaleOnlyCheckbox();
    }
  }
  
  // Also check state when USF is fully loaded
  if (usf && usf.event) {
    usf.event.add('sr_updated', function() {
      // Check if checkbox exists and sync state
      const checkbox = document.querySelector('#usf-sale-only-input');
      if (checkbox) {
        syncSaleOnlyCheckboxState();
      }
    });
  }
  
  // Override USF breadcrumb formatting for Sale Only filter
  function overrideSaleOnlyBreadcrumbFormatting() {
    // Wait for USF to load the formatBreadcrumbLabel function
    const checkForUSF = () => {
      if (typeof usf !== 'undefined' && usf.components && usf.components.FacetFilterBreadcrumb) {
        const originalMixins = usf.components.FacetFilterBreadcrumb.mixins || [];
        const breadcrumbMixin = originalMixins.find(m => m.methods && m.methods.formatBreadcrumbLabel);
        
        if (breadcrumbMixin && breadcrumbMixin.methods) {
          const originalFormatBreadcrumbLabel = breadcrumbMixin.methods.formatBreadcrumbLabel;
          
          // Override the formatBreadcrumbLabel method
          breadcrumbMixin.methods.formatBreadcrumbLabel = function(facet, facetName, queryValStr) {
            // Debug: log what we're receiving
            console.log('🔍 Breadcrumb format called:', {
              facetTitle: facet?.title,
              facetName: facetName,
              queryValStr: queryValStr
            });
            
            // Only override if there's ACTUALLY a problem with the value
            // Check for template strings, NaN, or other error patterns
            const hasTemplateString = queryValStr && (
              queryValStr.includes('{0}') || 
              queryValStr.includes('{1}')
            );
            
            const hasError = queryValStr && (
              queryValStr === 'NaN' ||
              queryValStr.includes('undefined') ||
              queryValStr.match(/^:\d+\s+:\d+$/) // Range format like ":1 :100"
            );
            
            // Only apply override if we detect an actual problem
            if (facet && facet.title && (
                facet.title.toLowerCase().includes('off') ||
                facet.title.toLowerCase().includes('%') ||
                facet.title.toLowerCase().includes('percent')
              ) && (hasTemplateString || hasError)) {
              
              console.log('🔥 Overriding problematic breadcrumb label for:', queryValStr);
              
              // Return a clean "Sale Only" label instead of the problematic template
              return 'Sale Only 🔥';
            }
            
            // For all other filters (including valid % OFF values), use the original function
            const result = originalFormatBreadcrumbLabel.call(this, facet, facetName, queryValStr);
            console.log('✅ Using original breadcrumb:', result);
            return result;
          };
          
          console.log('✅ Sale Only breadcrumb formatting override applied');
          return true;
        }
      }
      return false;
    };
    
    // Try to apply the override
    if (!checkForUSF()) {
      // If USF isn't ready yet, keep trying
      let attempts = 0;
      const maxAttempts = 50;
      
      const retryInterval = setInterval(() => {
        attempts++;
        if (checkForUSF() || attempts >= maxAttempts) {
          clearInterval(retryInterval);
          if (attempts >= maxAttempts) {
            console.log('⚠️ Could not override Sale Only breadcrumb formatting - USF not ready');
          }
        }
      }, 100);
    }
  }
  
  // Apply breadcrumb override (temporarily disabled for testing)
  // overrideSaleOnlyBreadcrumbFormatting();
  
  // Expose functions globally for debugging
  window.saleOnlyFunctions = {
    initializeSaleOnlyCheckbox,
    applySaleOnlyFilter,
    removeSaleOnlyFilter,
    applySaleOnlyFilterFallback,
    overrideSaleOnlyBreadcrumbFormatting
  };

})();
